/* eslint-disable @typescript-eslint/member-ordering */
/**
 * 轮询服务
 * 支持防重叠执行、错误重试、动态间隔调整
 */
export class PollingService {
    private intervalId: number | null = null;
    private intervalTime: number;
    private readonly callback: () => Promise<void>;
    private isRunning: boolean = false;
    private isExecuting: boolean = false; // ✅ 新增：防止重叠执行
    private retryCount: number = 0; // ✅ 新增：错误重试计数
    private readonly maxRetries: number = 3; // ✅ 新增：最大重试次数
    private executionCount: number = 0; // ✅ 新增：执行次数统计

    constructor(callback: () => Promise<void>, intervalTime: number = 5000) {
        this.callback = callback;
        this.intervalTime = intervalTime;
    }

    /** 开始轮询 */
    start() {
        // 防重：如果已经在运行，先停掉旧的
        if (this.isRunning) {
            // console.log('[PollingService] 检测到重复启动，先停止旧轮询');
            this.stop();
        }

        this.isRunning = true;
        this.executionCount = 0;
        this.retryCount = 0;

        // ✅ 立即执行第一次（不等待间隔）
        this.execute();

        // 设置定时器
        this.intervalId = window.setInterval(() => {
            this.execute();
        }, this.intervalTime);

        // console.log(`[PollingService] 轮询已启动，间隔 ${this.intervalTime}ms`);
    }

    /** 执行轮询回调（带防重叠和错误处理） */
    private async execute() {
        // 防止重叠执行
        if (this.isExecuting) {
            console.log('[PollingService] 上一次轮询还未完成，跳过本次执行');
            return;
        }

        this.isExecuting = true;
        this.executionCount++;
        const startTime = Date.now();

        try {
            // console.log(`[PollingService] 开始第 ${this.executionCount} 次轮询`);
            await this.callback();

            const duration = Date.now() - startTime;
            // console.log(`[PollingService] 第 ${this.executionCount} 次轮询完成，耗时 ${duration}ms`);

            // 成功后重置重试计数
            this.retryCount = 0;

            // 动态调整间隔（可选）
            this.adjustInterval(duration);

        } catch (error) {
            const duration = Date.now() - startTime;
            // console.error(`[PollingService] 第 ${this.executionCount} 次轮询失败，耗时 ${duration}ms`, error);

            // 错误重试逻辑
            this.handleError();

        } finally {
            this.isExecuting = false;
        }
    }

    /** 处理错误和重试 */
    private handleError() {
        this.retryCount++;

        if (this.retryCount >= this.maxRetries) {
            // console.error(`[PollingService] 连续失败 ${this.retryCount} 次，停止轮询`);
            this.stop();
            // 可以触发错误回调通知用户
            // this.onError?.();
        } else {
            // console.log(`[PollingService] 将在下次间隔后重试 (${this.retryCount}/${this.maxRetries})`);
        }
    }

    /** 动态调整轮询间隔（根据响应时间） */
    private adjustInterval(duration: number) {
        // 如果请求耗时超过间隔的 80%，增加间隔
        if (duration > this.intervalTime * 0.8) {
            const newInterval = Math.min(this.intervalTime * 1.5, 30000); // 最大 30 秒
            if (newInterval !== this.intervalTime) {
                console.log(`[PollingService] 请求耗时 ${duration}ms，调整间隔: ${this.intervalTime}ms → ${newInterval}ms`);
                this.updateInterval(newInterval);
            }
        }
    }

    /** 停止轮询 */
    stop() {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.isRunning = false;
        this.isExecuting = false;
        // console.log(`[PollingService] 轮询已停止，共执行 ${this.executionCount} 次`);
    }

    /** 更新轮询间隔 */
    updateInterval(intervalTime: number) {
        this.intervalTime = intervalTime;
        // 如果正在运行，重启以应用新间隔
        if (this.isRunning) {
            this.stop();
            this.start();
        }
    }

    /** 获取当前运行状态 */
    getIsRunning(): boolean {
        return this.isRunning;
    }

    /** 获取执行统计 */
    getStats() {
        return {
            isRunning: this.isRunning,
            isExecuting: this.isExecuting,
            executionCount: this.executionCount,
            retryCount: this.retryCount,
            intervalTime: this.intervalTime
        };
    }
}
